// ─────────────────────────────────────────────────────────────────────────────
// MoH Digital Systems Audit Framework — WHO-Aligned Types (v3)
// Based on WHO Global Strategy on Digital Health 2020-2025 and MAPS Toolkit
// ─────────────────────────────────────────────────────────────────────────────

// ── Maturity Model ──────────────────────────────────────────────────────────

/** WHO-aligned maturity levels (1–5) */
export enum MaturityLevel {
  AD_HOC = 1,
  REPEATABLE = 2,
  DEFINED = 3,
  MANAGED = 4,
  OPTIMIZING = 5,
}

export const MaturityLevelLabel: Record<MaturityLevel, string> = {
  [MaturityLevel.AD_HOC]: 'Ad-hoc',
  [MaturityLevel.REPEATABLE]: 'Repeatable',
  [MaturityLevel.DEFINED]: 'Defined',
  [MaturityLevel.MANAGED]: 'Managed',
  [MaturityLevel.OPTIMIZING]: 'Optimizing',
};

export const MaturityLevelDescription: Record<MaturityLevel, string> = {
  [MaturityLevel.AD_HOC]:
    'No formal processes. Activities are unstructured and ad-hoc.',
  [MaturityLevel.REPEATABLE]:
    'Basic processes established but not standardised across the organisation.',
  [MaturityLevel.DEFINED]:
    'Processes documented and standardised; roles and responsibilities are clear.',
  [MaturityLevel.MANAGED]:
    'Processes measured and controlled with quantitative metrics and KPIs.',
  [MaturityLevel.OPTIMIZING]:
    'Continuous improvement through data-driven optimisation and innovation.',
};

// ── Audit Domains (WHO Health System Building Blocks) ───────────────────────

/** The 8 audit domains aligned to WHO digital health framework */
export enum AuditDomain {
  GOVERNANCE_LEADERSHIP = 'governance-leadership',
  STRATEGY_INVESTMENT = 'strategy-investment',
  LEGISLATION_POLICY_COMPLIANCE = 'legislation-policy-compliance',
  WORKFORCE_CAPACITY = 'workforce-capacity',
  STANDARDS_INTEROPERABILITY = 'standards-interoperability',
  INFRASTRUCTURE = 'infrastructure',
  SERVICES_APPLICATIONS = 'services-applications',
  SECURITY_PRIVACY = 'security-privacy',
}

export const AuditDomainLabel: Record<AuditDomain, string> = {
  [AuditDomain.GOVERNANCE_LEADERSHIP]: 'Governance & Leadership',
  [AuditDomain.STRATEGY_INVESTMENT]: 'Strategy & Investment',
  [AuditDomain.LEGISLATION_POLICY_COMPLIANCE]:
    'Legislation, Policy & Compliance',
  [AuditDomain.WORKFORCE_CAPACITY]: 'Workforce & Capacity',
  [AuditDomain.STANDARDS_INTEROPERABILITY]: 'Standards & Interoperability',
  [AuditDomain.INFRASTRUCTURE]: 'Infrastructure',
  [AuditDomain.SERVICES_APPLICATIONS]: 'Services & Applications',
  [AuditDomain.SECURITY_PRIVACY]: 'Security & Privacy',
};

export const AuditDomainWHOBlock: Record<AuditDomain, string> = {
  [AuditDomain.GOVERNANCE_LEADERSHIP]: 'Leadership and governance',
  [AuditDomain.STRATEGY_INVESTMENT]: 'Health financing',
  [AuditDomain.LEGISLATION_POLICY_COMPLIANCE]: 'Leadership and governance',
  [AuditDomain.WORKFORCE_CAPACITY]: 'Health workforce',
  [AuditDomain.STANDARDS_INTEROPERABILITY]: 'Health information systems',
  [AuditDomain.INFRASTRUCTURE]: 'Service delivery',
  [AuditDomain.SERVICES_APPLICATIONS]: 'Service delivery',
  [AuditDomain.SECURITY_PRIVACY]: 'Health information systems',
};

// ── Domain Assessment ───────────────────────────────────────────────────────

export interface DomainAssessment {
  domain: AuditDomain;
  currentMaturity: MaturityLevel;
  targetMaturity: MaturityLevel;
  score: number; // 0-100 weighted score
  weightPercent: number; // Weight in overall score
  controlsTotal: number;
  controlsCompliant: number;
  controlsPartial: number;
  controlsNonCompliant: number;
  findingsCount: number;
  criticalFindings: number;
  lastAssessedDate: string;
  assessor: string;
  notes: string;
}

// ── Controls ────────────────────────────────────────────────────────────────

export type ComplianceStatus =
  | 'compliant'
  | 'partial'
  | 'non-compliant'
  | 'not-assessed';

export type ControlPriority = 'critical' | 'high' | 'medium' | 'low';

export interface AuditControl {
  id: string;
  domain: AuditDomain;
  controlRef: string; // e.g. "GOV-01", "SEC-03"
  title: string;
  description: string;
  whoReference: string; // WHO recommendation reference
  complianceStatus: ComplianceStatus;
  priority: ControlPriority;
  evidence: string[];
  gaps: string[];
  recommendations: string[];
  responsibleParty: string;
  dueDate?: string;
  lastReviewDate: string;
}

// ── Findings ────────────────────────────────────────────────────────────────

export type FindingSeverity =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'informational';
export type FindingStatus =
  | 'open'
  | 'in-progress'
  | 'remediated'
  | 'accepted-risk'
  | 'closed';
export type FindingCategory =
  | 'gap'
  | 'observation'
  | 'non-conformity'
  | 'opportunity-for-improvement'
  | 'good-practice';

export interface AuditFinding {
  id: string;
  domain: AuditDomain;
  controlRef: string;
  title: string;
  description: string;
  severity: FindingSeverity;
  status: FindingStatus;
  category: FindingCategory;
  affectedSystems: string[];
  rootCause: string;
  recommendation: string;
  remediationPlan?: string;
  owner: string;
  assignedTo: string;
  raisedDate: string;
  targetDate: string;
  closedDate?: string;
  evidence: string[];
  relatedFindings: string[];
}

// ── Audit Engagement ────────────────────────────────────────────────────────

export type AuditType = 'full' | 'targeted' | 'follow-up' | 'compliance-check';
export type AuditStatus =
  | 'planned'
  | 'in-progress'
  | 'review'
  | 'completed'
  | 'archived';

export interface AuditEngagement {
  id: string;
  title: string;
  auditType: AuditType;
  status: AuditStatus;
  scope: string;
  objectives: string[];
  framework: string; // "WHO Digital Health v3"
  leadAuditor: string;
  auditTeam: string[];
  startDate: string;
  endDate: string;
  reportDate?: string;
  domainAssessments: DomainAssessment[];
  findings: AuditFinding[];
  overallScore: number;
  overallMaturity: MaturityLevel;
}

// ── Dashboard Statistics ────────────────────────────────────────────────────

export interface AuditDashboardStats {
  overallScore: number;
  overallMaturity: MaturityLevel;
  totalControls: number;
  compliantControls: number;
  partialControls: number;
  nonCompliantControls: number;
  notAssessedControls: number;
  totalFindings: number;
  openFindings: number;
  criticalFindings: number;
  highFindings: number;
  remediatedFindings: number;
  overdueFindings: number;
  domainsAssessed: number;
  totalDomains: number;
  lastAuditDate: string;
  nextAuditDate: string;
  trendData: AuditTrendPoint[];
}

export interface AuditTrendPoint {
  period: string; // e.g. "Q1 2025"
  overallScore: number;
  maturityLevel: MaturityLevel;
  findingsOpen: number;
  findingsClosed: number;
}

// ── Compliance Summary ──────────────────────────────────────────────────────

export interface ComplianceSummary {
  domain: AuditDomain;
  totalControls: number;
  compliant: number;
  partial: number;
  nonCompliant: number;
  notAssessed: number;
  compliancePercent: number;
}

// ── Evidence & Artifacts ────────────────────────────────────────────────────

export type EvidenceType =
  | 'document'
  | 'screenshot'
  | 'interview'
  | 'system-output'
  | 'policy'
  | 'procedure';

export interface AuditEvidence {
  id: string;
  title: string;
  type: EvidenceType;
  domain: AuditDomain;
  controlRef: string;
  description: string;
  filePath?: string;
  url?: string;
  collectedDate: string;
  collectedBy: string;
}

// ── API Response Types ──────────────────────────────────────────────────────

export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}
